/**
 * The design-system contract — what a site's design system gives the builder.
 *
 * The builder's widgets (button, heading, card, input…) render through these
 * components, on the editor canvas and in the published page alike. Duro is
 * the first implementation (apps/builder-webapp/src/theme/duroModule.ts); a
 * site may later bring its own, which is loaded and run in isolation.
 *
 * The props are the SUBSET of Duro's the widgets actually pass, so matching
 * Duro's shape is enough. Published pages are server-rendered with no
 * JavaScript, so every component must render its markup without effects.
 */
import type { ComponentType, ReactNode } from "react";

/** Bumped when the contract changes incompatibly. */
export const CONTRACT_VERSION = 1;

/** The React major a design system module is built against (one React). */
export const REACT_MAJOR = 19;

export type Align = "start" | "center" | "end";

export interface DsButtonProps {
  readonly variant?:
    | "primary"
    | "secondary"
    | "inverseSecondary"
    | "link"
    | "danger";
  readonly size?: "default" | "small";
  readonly type?: "button" | "submit";
  /** Take the container's width. Optional to honour: a design system that
   *  ignores it renders the button at its own width. */
  readonly fullWidth?: boolean;
  readonly children: ReactNode;
}

export interface DsLinkButtonProps {
  readonly href: string;
  readonly variant?: "primary" | "secondary";
  readonly size?: "default" | "small";
  /** As DsButtonProps.fullWidth. */
  readonly fullWidth?: boolean;
  readonly children: ReactNode;
}

export interface DsHeadingProps {
  readonly level: 1 | 2 | 3 | 4 | 5 | 6;
  readonly align?: Align;
  readonly children: ReactNode;
}

export interface DsTextProps {
  readonly as?: "p" | "span";
  readonly children: ReactNode;
}

export interface DsCardProps {
  readonly variant?: "elevated" | "outlined" | "filled" | "interactive";
  readonly header?: string;
  readonly children: ReactNode;
}

export interface DsInlineProps {
  readonly justify?: Align;
  readonly children: ReactNode;
}

export interface DsInputProps {
  readonly type?: "text" | "search" | "email" | "url" | "tel";
  readonly name?: string;
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly inputMode?: "search";
  readonly "aria-label"?: string;
}

/** Glyphs an input may lead with. */
export const DS_ICONS = ["search", "mail", "lock", "key", "pin"] as const;
export type DsIconName = (typeof DS_ICONS)[number];

export interface DsIconProps {
  readonly name: DsIconName;
  readonly size?: "sm" | "md" | "lg";
}

export interface DsInputGroup {
  readonly Root: ComponentType<{ readonly children: ReactNode }>;
  readonly Addon: ComponentType<{
    readonly position?: "start" | "end";
    readonly children: ReactNode;
  }>;
}

export interface DsComponents {
  readonly Button: ComponentType<DsButtonProps>;
  readonly LinkButton: ComponentType<DsLinkButtonProps>;
  readonly Heading: ComponentType<DsHeadingProps>;
  readonly Text: ComponentType<DsTextProps>;
  readonly Card: ComponentType<DsCardProps>;
  readonly Inline: ComponentType<DsInlineProps>;
  readonly Input: ComponentType<DsInputProps>;
  readonly InputGroup: DsInputGroup;
  readonly Icon: ComponentType<DsIconProps>;
}

/** What a design system module exports (as its default export). */
export interface DesignSystemModule {
  readonly contractVersion: typeof CONTRACT_VERSION;
  /** Stable id (the theme-engine DesignSystem with the same id carries its
   *  default brand and tokens). */
  readonly id: string;
  readonly name: string;
  readonly reactMajor: typeof REACT_MAJOR;
  readonly components: DsComponents;
  /** Context the components need, if any. It must render no markup of its
   *  own: every widget is rendered inside it. */
  readonly Provider?: ComponentType<{ readonly children: ReactNode }>;
}

const COMPONENT_NAMES = [
  "Button",
  "LinkButton",
  "Heading",
  "Text",
  "Card",
  "Inline",
  "Input",
  "Icon",
] as const;

const isComponent = (v: unknown): boolean =>
  typeof v === "function" ||
  (typeof v === "object" && v !== null && "$$typeof" in v);

/**
 * Whether a value has the contract's shape: the right version and React
 * major, and every component present. Behaviour (does an Input render an
 * <input>?) is checked separately, by rendering.
 */
export function shapeErrors(value: unknown): string[] {
  if (typeof value !== "object" || value === null)
    return ["the module has no default export object"];
  const m = value as Record<string, unknown>;
  const errors: string[] = [];
  if (m["contractVersion"] !== CONTRACT_VERSION)
    errors.push(
      `contractVersion must be ${CONTRACT_VERSION} (got ${String(m["contractVersion"])})`,
    );
  if (m["reactMajor"] !== REACT_MAJOR)
    errors.push(
      `reactMajor must be ${REACT_MAJOR} (got ${String(m["reactMajor"])}) — build against React ${REACT_MAJOR}`,
    );
  for (const key of ["id", "name"] as const)
    if (typeof m[key] !== "string" || m[key] === "")
      errors.push(`${key} must be a non-empty string`);
  const c = m["components"] as Record<string, unknown> | undefined;
  if (!c || typeof c !== "object") return [...errors, "components is missing"];
  for (const name of COMPONENT_NAMES)
    if (!isComponent(c[name])) errors.push(`components.${name} is missing`);
  const group = c["InputGroup"] as Record<string, unknown> | undefined;
  if (!group || !isComponent(group["Root"]) || !isComponent(group["Addon"]))
    errors.push("components.InputGroup needs Root and Addon");
  if (m["Provider"] !== undefined && !isComponent(m["Provider"]))
    errors.push("Provider must be a component");
  return errors;
}
